<template>
  <vx-card title="Malware Creation">
    <p>Create a malware as you need it to simulate the attack.</p>

    <div class="mt-5">
      <form-wizard
        color="rgba(var(--vs-primary), 1)"
        errorColor="rgba(var(--vs-danger), 1)"
        :title="null"
        :subtitle="null"
        finishButtonText="Checkout"
        @on-change="formClearer"
      >
        <!-- Operating Systems -->
        <tab-content
          title="Target OS"
          class="mb-5"
          icon="feather icon-hard-drive"
          :before-change="validateStep1"
        >
          <form data-vv-scope="step-1">
            <div class="vx-row">
              <div
                class="w-full mt-10 mb-5 vx-col md:w-1/3"
                v-for="(os, index) in steps.step1.operatingSystems"
                :key="index"
              >
                <div :class="getOperatingSystemClass(os)"
                    @click="selectOperatingSystem(os)" class="relative flex flex-col items-center justify-center p-12 bg-white border shadow-xl rounded-xl hover:bg-grey-light">
                  <v-icon
                      name="check"
                      class="absolute text-primary"
                      style="top:5px; left: 5px;"
                      scale="1"
                      v-if="isOperatingSystemSelected(os)"
                    ></v-icon>
                    <v-icon
                      :name="`brands/${os.icon}`"
                      :title="os.name"
                      scale="4"
                      class="block mx-auto mb-3"
                    ></v-icon>
                    <div class="text-center">
                      <h4>{{os.name}}</h4>
                    </div>
                </div>
              </div>
            </div>
          </form>
        </tab-content>

        <!-- Languages -->
        <tab-content
          title="Language"
          class="mb-5"
          icon="feather icon-code"
          :before-change="validateStep2"
        >
          <form data-vv-scope="step-2">
            <transition name="slide-fade">
              <div class="vx-row">
                <div
                  class="w-full mt-10 mb-5 vx-col md:w-1/4"
                  v-for="(language, index) in steps.step2.languages"
                  :key="index"
                >

                  <div :class="getLanguageClass(language)"
                    @click="selectLanguage(language)" class="relative flex flex-col items-center justify-center p-12 bg-white border shadow-xl rounded-xl hover:bg-grey-light">
                    <v-icon
                      name="check"
                      class="absolute text-primary"
                      style="top: 5px; left: 5px;"
                      scale="1"
                      v-if="isLanguageSelected(language)"
                    ></v-icon>
                    <div class="block mx-auto mb-3 text-center">
                      <template v-if="language.logo">
                        <img
                          :src="getImage(language.logo)"
                          class="block p-1 rounded shadow-md cursor-pointer"
                          style="max-width: 150px; height: 6em"
                        />
                      </template>
                      <template v-else>
                        <i
                          :class="`icon-${language.icon}`"
                          class="w-12 h-12"
                          style="font-size: 4em"
                        ></i>
                      </template>
                    </div>
                    <div class="text-center">
                      <h4 class="capitalize">{{language.name}}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </transition>
          </form>
        </tab-content>

        <!-- Attack -->
        <tab-content
          title="Attack"
          class="mb-5"
          icon="fa fa-user-secret"
          :before-change="validateStep3"
        >
          <form data-vv-scope="step-4">
            <div class="vx-row">
              <template v-for="(attackType, index) in steps.step3.attackTypes">
                <div
                  class="w-full mt-10 mb-5 vx-col md:w-1/3"
                  v-if="attackType.enabled === true"
                  :key="index"
                >
                  <div :class="getAttackTypeClass(attackType)"
                    @click="selectAttackType(attackType)" class="relative flex flex-col items-center justify-center p-12 bg-white border shadow-xl rounded-xl hover:bg-grey-light">
                    <v-icon
                      name="check"
                      class="absolute text-primary"
                      style="top: 5px; left: 5px;"
                      scale="1"
                      v-if="isAttackTypeSelected(attackType)"
                    ></v-icon>
                    <div class="block mx-auto mb-3 text-center" style="height: 75px;">
                      <img
                        :src="attackType.logo"
                        :alt="attackType.name"
                        style="width:100%; height:100%; object-fit: contain"
                      />
                    </div>
                    <div class="text-center">
                      <h4>{{attackType.name}}</h4>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </form>
        </tab-content>

        <!-- Attack Detail -->
        <tab-content
          title="Attack Detail"
          class="mb-5"
          icon="fa fa-th-list"
          :before-change="validateStep4"
        >
          <form data-vv-scope="step-5">
            <div class="vx-row">
              <template v-if="this.steps.step3.selected.type === 'mitre'">
                <div class="w-full mt-10 mb-5 vx-col">
                  <vx-card>
                    <h3 class="mb-3 text-center">Mitre Att&ck Matrix</h3>
                    <vue-perfect-scrollbar class="scroll-area">
                      <table class="table">
                        <thead class="text-white bg-blue-800 thead">
                          <th
                            v-for="(attack, index) in this.steps.step4.mitreAttack.details"
                            :key="index"
                          >{{attack.title}}</th>
                        </thead>
                        <tbody>
                          <tr>
                            <td
                              v-for="(attack, index) in this.steps.step4.mitreAttack.details"
                              :key="index"
                            >
                              <div v-for="(technique, index) in attack.techniques" :key="index" style="font-size: .7rem;">
                                <template v-if="steps.step4.mitreAttack.selectable.indexOf(technique.id) !== -1 || isSubTechniqueAvailable(technique)">
                                  <div class="flex flex-col px-1 py-2 mb-4 text-center whitespace-no-wrap border border-dashed">
                                    <button :style="getTechniqueStyle(technique)" :class="getTechniqueClass(technique)" @click.prevent="selectDeselectTechnique(technique)" :disabled="steps.step4.mitreAttack.selectable.indexOf(technique.id) === -1" class="relative px-1 py-2 space-y-2 border hover:bg-success hover:text-white hover:border-transparent">
                                      <button @click.prevent="showTechniqueInfo(technique)" class="absolute top-0 right-0 flex items-center justify-center w-5 h-4 bg-gray-700 rounded-bl-lg">
                                        <i class="text-white fa fas fa-info"></i>
                                      </button>
                                      <p class="text-muted">{{technique.id}}</p>
                                      <p class="font-semibold text-blue-dark" >{{technique.name}} {{technique.sub_techniques.length > 0 ? `(${technique.sub_techniques.length})` : ''}}</p>
                                    </button>
                                    <div class="px-4">
                                      <button @click.prevent="technique.collapsed = !technique.collapsed" v-if="technique.sub_techniques.length > 0" class="px-2 py-2 font-semibold text-white bg-blue-500 border border-t-0 rounded-b-lg">{{technique.collapsed ? 'hide' : 'show'}} sub-techniques</button>
                                    </div>
                                    <div v-if="technique.collapsed" class="flex flex-col px-4 mt-2 space-y-2">
                                      <template v-for="sub_technique in technique.sub_techniques" >
                                        <button v-if="steps.step4.mitreAttack.selectable.indexOf(sub_technique.value) !== -1" :class="getSubtechniqueClass(sub_technique)" @click.prevent="selectDeselectSubtechnique(sub_technique)" :key="sub_technique.value" class="relative px-1 py-2 space-y-2 border hover:bg-success hover:text-white hover:border-transparent ">
                                          <button @click.prevent="showTechniqueInfo(sub_technique)" class="absolute top-0 right-0 flex items-center justify-center w-5 h-4 bg-gray-700 rounded-bl-lg">
                                            <i class="text-white fa fas fa-info"></i>
                                          </button>
                                          <p class="text-muted">{{technique.id}}{{sub_technique.id}}</p>
                                          <p class="font-semibold text-blue-dark" >{{sub_technique.name}}</p>
                                        </button>
                                      </template>
                                    </div>
                                  </div>
                                </template>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </vue-perfect-scrollbar>
                  </vx-card>
                </div>
              </template>
              <template v-else-if="this.steps.step3.selected.type === 'apt' && this.steps.step4.aptScenarios.selected.techniques">
                <div class="w-full mb-5 vx-col">
                  <h2 class="mb-2 text-3xl text-center text-gray-700">Threat Groups</h2>
                  <div class="overflow-hidden bg-white rounded-md shadow-xl">
                    <div class="flex px-1 py-2 mb-6 space-x-3 bg-gray-200 border">
                      <template v-for="aptScenario in this.steps.step4.aptScenarios.options">
                        <div :class="steps.step4.aptScenarios.selected._id === aptScenario._id ? 'bg-success text-white' : 'hover:bg-grey-light'" class="flex items-center justify-center px-4 py-4 rounded-sm cursor-pointer" :key="aptScenario._id" @click.prevent="steps.step4.aptScenarios.selected = aptScenario" >
                          <p>{{aptScenario.name}}</p>
                        </div>
                      </template>
                    </div>
                    <h3 class="mb-3 text-center text-gray-700">APT Scenario Mitre Att&ck Matrix</h3>
                    <vue-perfect-scrollbar class="px-6 scroll-area">
                      <table class="table">
                        <thead class="text-white bg-blue-800 thead">
                          <th
                            v-for="(attack, index) in this.steps.step4.aptScenarios.details"
                            :key="index"
                          >{{attack.title}}</th>
                        </thead>
                        <tbody>
                          <tr>
                            <td
                              v-for="(attack, index) in this.steps.step4.aptScenarios.details"
                              :key="index"
                            >
                              <div v-for="(technique, index) in attack.techniques" :key="index" style="font-size: .7rem;">
                                <template v-if="steps.step4.aptScenarios.selected.techniques.findIndex(t => t.id === technique.id) !== -1 || isSubTechniqueAvailableAPT(technique)">
                                  <div class="flex flex-col px-1 py-2 mb-4 text-center whitespace-no-wrap border border-dashed">
                                    <div :style="getTechniqueStyleAPT(technique)" :class="getTechniqueClassAPT(technique)" :disabled="steps.step4.aptScenarios.selected.techniques.findIndex(t => t.id === technique.id) === -1" class="relative px-1 py-2 space-y-2 border">
                                      <button @click.prevent="showTechniqueInfo(technique)" class="absolute top-0 right-0 flex items-center justify-center w-5 h-4 bg-gray-700 rounded-bl-lg">
                                        <i class="text-white fa fas fa-info"></i>
                                      </button>
                                      <p class="text-muted">{{technique.id}}</p>
                                      <p class="font-semibold text-blue-dark" >{{technique.name}} {{technique.sub_techniques.length > 0 ? `(${technique.sub_techniques.length})` : ''}}</p>
                                    </div>
                                    <div class="px-4">
                                      <button @click.prevent="technique.collapsed = !technique.collapsed" v-if="technique.sub_techniques.length > 0" class="px-2 py-2 font-semibold text-white bg-blue-500 border border-t-0 rounded-b-lg">{{technique.collapsed ? 'hide' : 'show'}} sub-techniques</button>
                                    </div>
                                    <div v-if="technique.collapsed" class="flex flex-col px-4 mt-2 space-y-2">
                                      <template v-for="sub_technique in technique.sub_techniques" >
                                        <div v-if="steps.step4.aptScenarios.selected.techniques.findIndex(t => t.id === sub_technique.value) !== -1" :key="sub_technique.value" class="relative px-1 py-2 space-y-2 text-white border bg-success">
                                          <button @click.prevent="showTechniqueInfo(sub_technique)" class="absolute top-0 right-0 flex items-center justify-center w-5 h-4 bg-gray-700 rounded-bl-lg">
                                            <i class="text-white fa fas fa-info"></i>
                                          </button>
                                          <p class="text-muted">{{technique.id}}{{sub_technique.id}}</p>
                                          <p class="font-semibold text-blue-dark" >{{sub_technique.name}}</p>
                                        </div>
                                      </template>
                                    </div>
                                  </div>
                                </template>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </vue-perfect-scrollbar>
                  </div>
                </div>
              </template>
              <template v-else>
                <div
                  class="w-full mt-10 mb-5 vx-col md:w-1/4"
                  v-for="(attack, index) in steps.step4.customAttack.customAttacks"
                  :key="index"
                >
                  <div :class="getCustomAttackClass(attack)"
                    @click="selectCustomAttack(attack)" class="relative flex flex-col items-center justify-center p-12 bg-white border shadow-xl rounded-xl hover:bg-grey-light">
                    <v-icon
                      name="check"
                      class="absolute text-primary"
                      style="top: 5px; left: 5px;"
                      scale="1"
                      v-if="isCustomAttackSelected(attack)"
                    ></v-icon>
                    <div class="block mx-auto mb-3 text-center" style="height: 75px;">
                      <img
                        :src="getImage(attack.logo)"
                        :alt="attack.name"
                        style="width:100%; height:100%; object-fit: contain"
                      />
                    </div>
                    <div class="text-center">
                      <h4>{{attack.name}}</h4>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </form>
          <vs-popup :title="steps.step4.mitreAttack.popup.data.name" :active.sync="steps.step4.mitreAttack.popup.active">
          <p>{{steps.step4.mitreAttack.popup.data.description}}</p>
          </vs-popup>
        </tab-content>

        <!-- Protections -->
        <tab-content
          title="Protection"
          class="mb-5"
          icon="feather icon-shield"
          :before-change="validateStep5"
        >
          <form data-vv-scope="step-3">
            <div class="vx-row">
              <div
                class="w-full mt-10 mb-5 vx-col md:w-1/4"
                v-for="(protection, index) in steps.step5.protections"
                :key="index"
              >
                <vx-card
                  class="p-2"
                  :class="getProtectionClass(protection)"
                  @click="selectProtection(protection)"
                >
                  <v-icon
                    name="check"
                    class="float-right text-primary"
                    scale="1"
                    v-if="isProtectionSelected(protection)"
                  ></v-icon>
                  <br />
                  <div class="block mx-auto my-3 text-center" style="height: 75px;">
                    <img
                      :src="getImage(protection.logo)"
                      :alt="protection.name"
                      style="width:100%; height:100%; object-fit: contain"
                    />
                  </div>
                  <div class="mb-1 text-center">
                    <h4>{{protection.name}}</h4>
                  </div>
                  <template slot="footer">
                    <vs-divider />
                    <div class="flex justify-between">
                      <span class="flex items-center">
                        <feather-icon
                          icon="ShieldIcon"
                          svgClasses="h-5 w-5 text-warning stroke-current"
                        />
                      </span>
                      <span class="flex items-center">
                        <feather-icon
                          icon="CodeIcon"
                          svgClasses="h-5 w-5 text-warning stroke-current"
                        />
                        <span class="ml-2">{{protection.version}}</span>
                      </span>
                    </div>
                  </template>
                </vx-card>
              </div>
            </div>
          </form>
        </tab-content>

        <!-- Preview & Submit -->
        <tab-content title="Preview" class="mb-5" icon="fa fa-check" :before-change="validateStep6">
          <form data-vv-scope="step-6">
            <div class="vx-row">
              <!-- LEFT COL -->

              <vx-card>
                <div class="vx-row">
                  <div class="relative w-full vx-col lg:w-1/2">
                    <!-- component -->
                    <div class="flex flex-col items-center justify-center">
                      <h3 class="text-1xl">Simulation Flow Chart</h3>
                      <div class="container pt-12 mx-auto text-center">
                        <div class="flex items-center justify-center">
                          <div class="text-center">
                            <div class="flex flex-col items-center justify-center">
                              <div class="w-32 h-16">
                                <v-icon
                                  :name="`brands/${steps.step1.selected.icon}`"
                                  :title="steps.step1.selected.name"
                                  scale="3"
                                  class="block m-auto"
                                ></v-icon>
                              </div>
                              <div>
                                <p>{{steps.step1.selected.name}}</p>
                              </div>
                            </div>
                            <ul class="flex flex-row justify-center mt-10">
                              <div class="absolute h-10 -mt-10 border-l-2 border-gray-400"></div>
                              <li class="relative p-6">
                                <div
                                  class="absolute top-0 h-8 border-t-2 border-gray-400"
                                  style="left: 50%; right: 50%;"
                                ></div>
                                <div class="relative flex justify-center">
                                  <div class="absolute top-0 h-6 -mt-6 border-l-2 border-gray-400"></div>
                                  <div class="text-center">
                                    <div class="flex flex-col items-center justify-center">
                                      <div class="w-32 h-16">
                                        <template v-if="steps.step2.selected.logo">
                                          <img
                                            :src="getImage(steps.step2.selected.logo)"
                                            class="block p-1 rounded shadow-md cursor-pointer"
                                            style="max-width: 150px; height: 50px"
                                          />
                                        </template>
                                        <template v-else>
                                          <i
                                            :class="`icon-${steps.step2.selected.icon}`"
                                            class="block m-auto"
                                            style="font-size: 3em"
                                          ></i>
                                        </template>
                                      </div>
                                      <div class="text-gray-600">
                                        <p>{{steps.step2.selected.name}}</p>
                                      </div>
                                    </div>
                                    <ul class="flex flex-row justify-center mt-10">
                                      <div class="absolute h-10 -mt-10 border-l-2 border-gray-400"></div>
                                      <li class="relative p-6">
                                        <div
                                          class="absolute top-0 h-8 border-t-2 border-gray-400"
                                          style="left: 50%; right: 0px;"
                                        ></div>
                                        <div class="relative flex justify-center">
                                          <div
                                            class="absolute top-0 h-6 -mt-6 border-l-2 border-gray-400"
                                          ></div>
                                          <div class="text-center">
                                            <div class="flex flex-col items-center justify-center">
                                              <div class="w-32 h-16">
                                                <img
                                                  :src="getImage(steps.step5.selected.logo)"
                                                  class="block m-auto"
                                                  style="width:100%; height:100%; object-fit: contain"
                                                />
                                              </div>
                                              <div class="text-gray-600">
                                                <p>{{steps.step5.selected.name}}</p>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </li>
                                      <li class="relative p-6">
                                        <div
                                          class="absolute top-0 h-8 border-t-2 border-gray-400"
                                          style="left: 0px; right: 50%;"
                                        ></div>
                                        <div class="relative flex justify-center">
                                          <div
                                            class="absolute top-0 h-6 -mt-6 border-l-2 border-gray-400"
                                          ></div>
                                          <div class="text-center">
                                            <div class="flex flex-col items-center justify-center">
                                              <div class="w-32 h-16">
                                                <img
                                                  class="block m-auto"
                                                  :src="steps.step3.selected.logo"
                                                  :alt="steps.step3.selected.name"
                                                  style="width:100%; height:100%; object-fit: contain"
                                                />
                                              </div>
                                              <div class="text-gray-600">
                                                <p>{{steps.step3.selected.name}}</p>
                                              </div>
                                            </div>
                                            <ul
                                              class="flex flex-row justify-center mt-10"
                                              v-if="steps.step3.selected.type === 'custom'"
                                            >
                                              <div
                                                class="absolute h-10 -mt-10 border-l-2 border-gray-400"
                                              ></div>
                                              <li class="relative p-6">
                                                <div
                                                  class="absolute top-0 h-8 border-t-2 border-gray-400"
                                                  style="left: 50%; right: 50%;"
                                                ></div>
                                                <div class="relative flex justify-center">
                                                  <div
                                                    class="absolute top-0 h-6 -mt-6 border-l-2 border-gray-400"
                                                  ></div>
                                                  <div class="text-center">
                                                    <div
                                                      class="flex flex-col items-center justify-center"
                                                    >
                                                      <div class="w-32 h-16">
                                                        <img
                                                          class="block m-auto"
                                                          :src="getImage(steps.step4.customAttack.selected.logo)"
                                                          :alt="steps.step4.customAttack.selected.name"
                                                          style="width:100%; height:100%; object-fit: contain"
                                                        />
                                                      </div>
                                                      <div class="text-gray-600">
                                                        <p>{{steps.step4.customAttack.selected.name}}</p>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              </li>
                                            </ul>
                                             <ul
                                              class="flex flex-row justify-center mt-10"
                                              v-if="steps.step3.selected.type === 'apt'"
                                            >
                                              <div
                                                class="absolute h-10 -mt-10 border-l-2 border-gray-400"
                                              ></div>
                                              <li class="relative p-6">
                                                <div
                                                  class="absolute top-0 h-8 border-t-2 border-gray-400"
                                                  style="left: 50%; right: 50%;"
                                                ></div>
                                                <div class="relative flex justify-center">
                                                  <div
                                                    class="absolute top-0 h-6 -mt-6 border-l-2 border-gray-400"
                                                  ></div>
                                                  <div class="text-center">
                                                    <div
                                                      class="flex flex-col items-center justify-center"
                                                    >
                                                      <div class="text-gray-600">
                                                        <p>{{steps.step4.aptScenarios.selected.name}}</p>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              </li>
                                            </ul>
                                          </div>
                                        </div>
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- RIGHT COL -->
                  <div class="relative w-full vx-col lg:w-1/2">
                    <div class="block mx-auto my-3 text-center" style="height: 200px;">
                      <img
                        :src="require('@/assets/images/svg/data-processing.svg')"
                        style="width:100%; height:100%; object-fit: contain"
                      />
                    </div>
                    <div class="container flex flex-col justify-center pt-12 mx-auto text-center">
                      <p class="mb-3 font-semibold text-center">Price Details</p>
                      <div class="flex justify-between mb-2">
                        <span>Operating System</span>
                        <span class="font-semibold">{{steps.step1.selected.name}}</span>
                      </div>
                      <div class="flex justify-between mb-2">
                        <span>Programming Language</span>
                        <span class="font-semibold">{{steps.step2.selected.name}}</span>
                      </div>
                      <div class="flex justify-between mb-2">
                        <span>Protection</span>
                        <span class="font-semibold">{{steps.step5.selected.name}}</span>
                      </div>
                      <div class="flex justify-between mb-2">
                        <span>Attack Type</span>
                        <span class="font-semibold">{{steps.step3.selected.name}}</span>
                      </div>
                      <div
                        class="flex justify-between mb-2"
                        v-if="steps.step3.selected.type === 'mitre'"
                      >
                        <span>Techniques</span>
                        <span
                          class="font-semibold"
                        >{{steps.step4.mitreAttack.selected.length > 3 ? steps.step4.mitreAttack.selected.slice(0,3)+"..." : steps.step4.mitreAttack.selected}}</span>
                      </div>
                      <div
                        class="flex justify-between mb-2"
                        v-if="steps.step3.selected.type === 'apt' && steps.step4.aptScenarios.selected.techniques"
                      >
                        <span>Techniques</span>
                        <span
                          class="font-semibold"
                        >{{steps.step4.aptScenarios.selected.techniques.map(t => t.id).length > 3 ? steps.step4.aptScenarios.selected.techniques.map(t => t.id).slice(0,3)+"..." : steps.step4.aptScenarios.selected.techniques.map(t => t.id)}}</span>
                      </div>
                      <div class="flex justify-between mb-2" v-if="steps.step3.selected.type === 'apt'">
                        <span>Scenario Name</span>
                        <span class="font-semibold">{{steps.step4.aptScenarios.selected.name}}</span>
                      </div>
                      <div class="flex justify-between mb-2" v-if="steps.step3.selected.type === 'custom'">
                        <span>Custom Attack</span>
                        <span class="font-semibold">{{steps.step4.customAttack.selected.name}}</span>
                      </div>

                      <vs-divider />
                    </div>
                  </div>
                </div>
              </vx-card>
            </div>
          </form>
        </tab-content>
      </form-wizard>
    </div>
  </vx-card>
</template>

<script>
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import mitreData from '../mitreDataUpdated'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'

export default {
  data () {
    return {
      steps: {
        step1: {
          selected: {},
          operatingSystems: []
        },
        step2: {
          selected: {},
          languages: []
        },
        step3: {
          selected: {},
          attackTypes: [
            {
              id: 1,
              logo: require('@/assets/images/mitre.png'),
              type: 'mitre',
              name: 'Mitre Att&ck',
              enabled: false
            },
            {
              id: 2,
              logo: require('@/assets/images/custom-attack.png'),
              type: 'custom',
              name: 'Custom Attack',
              enabled: false
            }, {
              id: 3,
              logo: require('@/assets/images/svg/apt.svg'),
              type: "apt",
              name: "APT Scenario",
              enabled: false
            }
          ]
        },
        step4: {
          aptScenarios: {
            selected: {},
            options: [],
            details: []
          },
          mitreAttack: {
            selected: [],
            selectable: [],
            details: [],
            attack_id: null,
            popup: {
              active: false,
              data: {
                name: '',
                description: ''
              }
            }
          },
          customAttack: {
            selected: {},
            customAttacks: []
          }
        },
        step5: {
          selected: {},
          protections: []
        },
      }
    }
  },
  watch: {
    "steps.step4.mitreAttack.popup.active": {
      handler(val) {
        if(!val){
          this.steps.step4.mitreAttack.popup.data = {
            name: '',
            description: ''
          };
        }
      },
      deep: true
    }
  },
  methods: {
    showTechniqueInfo(technique){
      this.steps.step4.mitreAttack.popup.active = true;
      this.steps.step4.mitreAttack.popup.data = technique;
    },
    isSubTechniqueAvailableAPT(technique) {
      if(technique.sub_techniques.length > 0){
        let available = false;
        for (let index = 0; !available && index < technique.sub_techniques.length; index++) {
          let sub_technique = technique.sub_techniques[index];
          if(this.steps.step4.aptScenarios.selected.techniques.findIndex(t => t.id === sub_technique.value) !== -1){
            available = true;
          }
        }
        return available;
      }
      return false
    },
    isSubTechniqueAvailable(technique){
      if(technique.sub_techniques.length > 0){
        let available = false;
        for (let index = 0; !available && index < technique.sub_techniques.length; index++) {
          let sub_technique = technique.sub_techniques[index];
          if(this.steps.step4.mitreAttack.selectable.indexOf(sub_technique.value) !== -1){
            available = true;
          }
        }
        return available;
      }
      return false
    },
    clearLanguages () {
      this.steps.step2.selected = {}
      this.steps.step2.languages = []
    },
    clearProtections () {
      this.steps.step5.selected = {}
      this.steps.step5.protections = []
    },
    clearAttackTypes () {
      this.steps.step3.selected = {}
    },
    clearAttackDetails () {
      this.steps.step4.mitreAttack.selected = []
      this.steps.step4.mitreAttack.selectable = []
      this.steps.step4.mitreAttack.details = []
      this.steps.step4.customAttack.selected = []
      this.steps.step4.customAttack.customAttacks = []
    },
    formClearer (prev, current) {
      if (prev && current === 0) {
        this.clearLanguages()
        this.clearProtections()
        this.clearAttackTypes()
        this.clearAttackDetails()
      } else if (prev > current && current === 1) {
        this.clearAttackTypes()
        this.clearAttackDetails()
        this.clearProtections()
      } else if (prev > current && current === 2) {
        this.clearAttackDetails()
        this.clearProtections()
      } else if (prev > current && current === 3) {
        this.clearProtections()
      }
    },
    getImage (image_id) {
      return `${process.env.VUE_APP_BASE_URL}/images/${image_id}`
    },
    isObjectEmpty (obj) {
      if (Object.keys(obj).length === 0 && obj.constructor === Object) {
        return true
      }
      return false
    },
    getOperatingSystemClass (os) {
      if (this.steps.step1.selected._id === os._id) {
        return 'selected'
      }
      return 'selectable'
    },
    selectOperatingSystem (os) {
      // If object is empty or selected different os, select new one.
      if (
        this.isObjectEmpty(this.steps.step1.selected) ||
        this.steps.step1.selected._id !== os._id
      ) {
        this.steps.step1.selected = os
      } else {
        // Deselect selected one
        this.steps.step1.selected = {}
      }
    },
    isOperatingSystemSelected (os) {
      if (this.steps.step1.selected._id === os._id) {
        return true
      }
      return false
    },
    validateStep1 () {
      if (!this.isObjectEmpty(this.steps.step1.selected)) {
        this.$http
          .get(`/user/languages/${this.steps.step1.selected._id}`)
          .then((res) => {
            if (res.data.status === true) {
              this.$vs.loading()
              setTimeout(() => {
                this.steps.step2.languages = res.data.languages
                this.$vs.loading.close()
              }, 250)
            }
          })
        return true
      }
      this.$swal({
        position: 'bottom-end',
        icon: 'warning',
        toast: true,
        title: 'Please select an operating system to continue',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
      })
      return false
    },
    getLanguageClass (language) {
      if (this.steps.step2.selected._id === language._id) {
        return 'selected'
      }
      return 'selectable'
    },
    selectLanguage (language) {
      // If object is empty or selected different language, select new one.
      if (
        this.isObjectEmpty(this.steps.step2.selected) ||
        this.steps.step2.selected._id !== language._id
      ) {
        this.steps.step2.selected = language
      } else {
        // Deselect selected one
        this.steps.step2.selected = {}
      }
    },
    isLanguageSelected (language) {
      if (this.steps.step2.selected._id === language._id) {
        return true
      }
      return false
    },
    validateStep2 () {
      if (!this.isObjectEmpty(this.steps.step2.selected)) {
        this.$http
          .get(`/user/attacks/${this.steps.step2.selected._id}`)
          .then((res) => {
            if (res.data.status === true) {
              this.$vs.loading()
              setTimeout(() => {
                this.steps.step3.attackTypes.forEach((attack) => {
                  attack.enabled = res.data[attack.type] === true
                })
                this.$vs.loading.close()
              }, 250)
            }
          })
        return true
      }
      this.$swal({
        position: 'bottom-end',
        icon: 'warning',
        toast: true,
        title: 'Please select a programming language to continue',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
      })
      return false
    },
    getProtectionClass (protection) {
      if (this.steps.step5.selected._id === protection._id) {
        return 'selected'
      }
      return 'selectable'
    },
    selectProtection (protection) {
      // If object is empty or selected different language, select new one.
      if (
        this.isObjectEmpty(this.steps.step5.selected) ||
        this.steps.step5.selected._id !== protection._id
      ) {
        this.steps.step5.selected = protection
      } else {
        // Deselect selected one
        this.steps.step5.selected = {}
      }
    },
    isProtectionSelected (protection) {
      if (this.steps.step5.selected._id === protection._id) {
        return true
      }
      return false
    },
    validateStep5 () {
      if (!this.isObjectEmpty(this.steps.step5.selected)) {
        return true
      }
      this.$swal({
        position: 'bottom-end',
        icon: 'warning',
        toast: true,
        title: 'Please select a protection application to continue',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
      })
      return false
    },
    getAttackTypeClass (attackType) {
      if (this.steps.step3.selected.id === attackType.id) {
        return 'selected'
      }
      return 'selectable'
    },
    selectAttackType (attackType) {
      // If object is empty or selected different language, select new one.
      if (
        this.isObjectEmpty(this.steps.step3.selected) ||
        this.steps.step3.selected.id !== attackType.id
      ) {
        this.steps.step3.selected = attackType
      } else {
        // Deselect selected one
        this.steps.step3.selected = {}
      }
    },
    isAttackTypeSelected (attackType) {
      if (this.steps.step3.selected.id === attackType.id) {
        return true
      }
      return false
    },
    validateStep3 () {
      if (!this.isObjectEmpty(this.steps.step3.selected)) {
        if (this.steps.step3.selected.type === 'mitre') {
          this.steps.step4.mitreAttack.selected = []
          this.steps.step4.mitreAttack.selectable = []
          this.steps.step4.mitreAttack.details = []
          this.steps.step4.mitreAttack.details = JSON.parse(
            JSON.stringify(mitreData)
          )
          this.$http
            .get(`/user/attacks/${this.steps.step2.selected._id}/mitre`)
            .then((res) => {
              if (res.data.status === true) {
                this.$vs.loading()
                setTimeout(() => {
                  this.steps.step4.mitreAttack.attack_id = res.data.attack._id;
                  this.steps.step4.mitreAttack.selectable = []
                  res.data.attack.techniques.forEach((technique) => {
                    this.steps.step4.mitreAttack.selectable.push(technique.id)
                  })
                  this.$vs.loading.close()
                }, 250)
              }
            })
        } else if(this.steps.step3.selected.type === 'apt') {
            this.steps.step4.aptScenarios.options = []
            this.steps.step4.aptScenarios.selected = {};
            this.steps.step4.aptScenarios.details = JSON.parse(
              JSON.stringify(mitreData)
            )
            this.$http
              .get(`/user/attacks/${this.steps.step2.selected._id}/apt`)
              .then((res) => {
                if (res.data.status === true) {
                  this.$vs.loading()
                  setTimeout(() => {
                    this.steps.step4.aptScenarios.options = res.data.attack;
                    this.steps.step4.aptScenarios.selected = res.data.attack[0];
                    this.$vs.loading.close()
                  }, 250)
                }
              })
        }else if (this.steps.step3.selected.type === 'custom') {
          this.$http
            .get(`/user/attacks/${this.steps.step2.selected._id}/custom`)
            .then((res) => {
              if (res.data.status === true) {
                this.$vs.loading()
                setTimeout(() => {
                  this.steps.step4.customAttack.customAttacks =
                    res.data.attacks
                  this.$vs.loading.close()
                }, 250)
              }
            })
        } else {
          return false
        }
        return true
      }
      this.$swal({
        position: 'bottom-end',
        icon: 'warning',
        toast: true,
        title: 'Please select an attack type to continue',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
      })
      return false
    },
    getTechniqueStyleAPT (technique) {
      if (
        this.steps.step4.aptScenarios.selected.techniques.findIndex(t => t.id === technique.id) === -1
      ) {
        return 'color: gray; cursor: not-allowed;'
      }
    },
    getTechniqueStyle (technique) {
      if (
        this.steps.step4.mitreAttack.selectable.indexOf(technique.id) === -1
      ) {
        return 'color: gray; cursor: not-allowed;'
      }
    },
    getTechniqueClassAPT (technique) {
      if (
        this.steps.step4.aptScenarios.selected.techniques.findIndex(t => t.id === technique.id) !== -1
      ) {
        return 'bg-success text-white'
      } else {
        return 'hover:bg-success'
      }
    },
    getTechniqueClass (technique) {
      if (
        this.steps.step4.mitreAttack.selectable.indexOf(technique.id) !== -1
      ) {
        if (
          this.steps.step4.mitreAttack.selected.indexOf(technique.id) !== -1
        ) {
          return 'bg-success text-white'
        } else {
          return 'hover:bg-success'
        }
      }
    },
    getSubtechniqueClassAPT(sub_technique){
      if(this.steps.step4.aptScenarios.selected.techniques.findIndex(t => t.id === sub_technique.id) !== -1){
        return 'bg-success text-white'
      } else {
        return 'hover:bg-success'
      }
    },
    getSubtechniqueClass(sub_technique){
      if(this.steps.step4.mitreAttack.selectable.indexOf(sub_technique.value) !== -1){
        if(this.steps.step4.mitreAttack.selected.indexOf(sub_technique.value) !== -1){
          return 'bg-success text-white'
        } else {
          return 'hover:bg-success'
        }
      }
    },
    selectDeselectTechnique (technique) {
      if (this.steps.step4.mitreAttack.selected.indexOf(technique.id) === -1) {
        if (
          this.steps.step4.mitreAttack.selectable.indexOf(technique.id) !== -1
        ) {
          this.steps.step4.mitreAttack.selected.push(technique.id)
        }
      } else {
        this.steps.step4.mitreAttack.selected.splice(
          this.steps.step4.mitreAttack.selected.indexOf(technique.id),
          1
        )
      }
    },
    selectDeselectSubtechnique(subtechnique){
      if(this.steps.step4.mitreAttack.selected.indexOf(subtechnique.value) === -1){
        if(this.steps.step4.mitreAttack.selectable.indexOf(subtechnique.value) !== -1){
          this.steps.step4.mitreAttack.selected.push(subtechnique.value);
        }
      } else {
        this.steps.step4.mitreAttack.selected.splice(this.steps.step4.mitreAttack.selected.indexOf(subtechnique.value), 1)
      }
    },
    getCustomAttackClass (customAttack) {
      if (this.steps.step4.customAttack.selected._id === customAttack._id) {
        return 'selected'
      }
      return 'selectable'
    },
    selectCustomAttack (customAttack) {
      // If object is empty or selected different language, select new one.
      if (
        this.isObjectEmpty(this.steps.step4.customAttack.selected) ||
        this.steps.step4.customAttack.selected._id !== customAttack._id
      ) {
        this.steps.step4.customAttack.selected = customAttack
      } else {
        // Deselect selected one
        this.steps.step4.customAttack.selected = {}
      }
    },
    isCustomAttackSelected (customAttack) {
      if (this.steps.step4.customAttack.selected._id === customAttack._id) {
        return true
      }
      return false
    },
    async validateStep4 () {
      if (this.steps.step3.selected.type === 'mitre') {
        try {
          const mitreSize = (await this.$http.get('/user/profile')).data.user
            .packageType.mitreSize
          if (
            (mitreSize >= this.steps.step4.mitreAttack.selected.length ||
              mitreSize === -1) &&
            this.steps.step4.mitreAttack.selected.length > 0
          ) {
            this.$http
              .get(`/user/protections/${this.steps.step4.mitreAttack.attack_id}`)
              .then((res) => {
                if (res.data.status === true) {
                  this.$vs.loading()
                  setTimeout(() => {
                    this.steps.step5.protections = res.data.protections
                    this.$vs.loading.close()
                  }, 250)
                }
              })
            return true
          } else if (
            this.steps.step4.mitreAttack.selected.length > mitreSize &&
            mitreSize !== -1
          ) {
            this.$swal({
              position: 'bottom-end',
              icon: 'warning',
              toast: true,
              title: `You cannot select more than ${mitreSize} mitre attack techniques with your current package.`,
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true
            })
          } else if (this.steps.step4.mitreAttack.selected.length <= 0) {
            this.$swal({
              position: 'bottom-end',
              icon: 'warning',
              toast: true,
              title:
                'You must select at least 1 Mitre Att&ck technique to continue.',
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true
            })
          }
        } catch (err) {
          this.$swal({
            position: 'bottom-end',
            icon: 'warning',
            toast: true,
            title:
              'Seems like we\'ve having problem in our attack system, please contact with administrator.',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true
          })
          return false
        }
      } else if (this.steps.step3.selected.type === 'apt') {
        if(!this.isObjectEmpty(this.steps.step4.aptScenarios.selected)) {
          this.$http
            .get(`/user/protections/${this.steps.step4.aptScenarios.selected._id}`)
            .then((res) => {
              if (res.data.status === true) {
                this.$vs.loading()
                setTimeout(() => {
                  this.steps.step5.protections = res.data.protections
                  this.$vs.loading.close()
                }, 250)
              }
            })
          return true
        }
      } else if (this.steps.step3.selected.type === 'custom') {
        if (!this.isObjectEmpty(this.steps.step4.customAttack.selected)) {
          this.$http
            .get(`/user/protections/${this.steps.step4.customAttack.selected._id}`)
            .then((res) => {
              if (res.data.status === true) {
                this.$vs.loading()
                setTimeout(() => {
                  this.steps.step5.protections = res.data.protections
                  this.$vs.loading.close()
                }, 250)
              }
            })
          return true
        }
        this.$swal({
          position: 'bottom-end',
          icon: 'warning',
          toast: true,
          title: 'You must select a custom attack to continue.',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true
        })
      } else {
        this.$swal({
          position: 'bottom-end',
          icon: 'warning',
          toast: true,
          title:
            'Seems like we\'ve having problem in our attack system, please contact with administrator.',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true
        })
      }
      return false
    },
    validateStep6 () {
      this.$swal({
        title: 'Do you really want to initialize the simulation?',
        text: 'You won\'t be able to revert this!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, initialize it!'
      }).then((result) => {
        if (result.value) {
          let data = {
            operatingSystem: this.steps.step1.selected._id,
            language: this.steps.step2.selected._id,
            protection: this.steps.step5.selected._id,
            attackType: this.steps.step3.selected.type
          }
          if (this.steps.step3.selected.type === 'mitre') {
            data = Object.assign(data, {
              techniques: this.steps.step4.mitreAttack.selected,
              mitre_attack_id: this.steps.step4.mitreAttack.attack_id
            })
          } else if (this.steps.step3.selected.type === 'apt') {
            data = Object.assign(data, {
              attackName: this.steps.step4.aptScenarios.selected
            })
          } else {
            data = Object.assign(data, {
              attackName: this.steps.step4.customAttack.selected
            })
          }
          this.$http
            .post('/user/simulations', data)
            .then((res) => {
              if (res.data.status === true) {
                this.$swal({
                  position: 'bottom-start',
                  icon: 'success',
                  toast: true,
                  title: 'Simulation successfully created.',
                  showConfirmButton: false,
                  timer: 3000,
                  timerProgressBar: true
                })
                this.$router.push('/track')
              } else {
                this.$swal({
                  position: 'bottom-start',
                  icon: 'error',
                  toast: true,
                  title: 'Error occurred creating the simulation.',
                  showConfirmButton: false,
                  timer: 3000,
                  timerProgressBar: true
                })
              }
            })
            .catch((err) => {
              this.$swal({
                position: 'bottom-start',
                icon: 'error',
                toast: true,
                title: err.response.data.message,
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
              })
            })
        }
      })
    }
  },
  created () {
    this.$http.get('/user/operating-systems').then((res) => {
      if (res.data.status === true) {
        this.steps.step1.operatingSystems = res.data.operatingSystems
      }
    })
  },
  components: {
    FormWizard,
    TabContent,
    VuePerfectScrollbar
  }
}
</script>
<style>
.selectable {
  transition: background-color 0.3s ease-in 0.1s;
}

.selectable:hover {
  cursor: pointer;
  background-color: rgba(128, 128, 128, 0.171);
  color: #096c8a;
}

.selected {
  box-shadow: 0px 1px 3px 2px rgba(6, 75, 121, 0.493);
  color: #096c8a;
  cursor: pointer;
}
.table {
  width: 100%;
  margin-bottom: 1rem;
  color: #212529;
}

.table th,
.table td {
  padding: 0.75rem;
  vertical-align: top;
  border-top: 1px solid #dee2e6;
}

.table thead th {
  text-align: center;
  vertical-align: bottom;
  border-bottom: 2px solid #dee2e6;
}

.table tbody + tbody {
  border-top: 2px solid #dee2e6;
}

.table-sm th,
.table-sm td {
  padding: 0.3rem;
}

.table-bordered {
  border: 1px solid #dee2e6;
}

.table-bordered th,
.table-bordered td {
  border: 1px solid #dee2e6;
}

.table-bordered thead th,
.table-bordered thead td {
  border-bottom-width: 2px;
}

.table-borderless th,
.table-borderless td,
.table-borderless thead th,
.table-borderless tbody + tbody {
  border: 0;
}

.table-striped tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.05);
}

.table-hover tbody tr:hover {
  color: #212529;
  background-color: rgba(0, 0, 0, 0.075);
}

.table-primary,
.table-primary > th,
.table-primary > td {
  background-color: #b8daff;
}

.table-primary th,
.table-primary td,
.table-primary thead th,
.table-primary tbody + tbody {
  border-color: #7abaff;
}

.table-hover .table-primary:hover {
  background-color: #9fcdff;
}

.table-hover .table-primary:hover > td,
.table-hover .table-primary:hover > th {
  background-color: #9fcdff;
}

.table-secondary,
.table-secondary > th,
.table-secondary > td {
  background-color: #d6d8db;
}

.table-secondary th,
.table-secondary td,
.table-secondary thead th,
.table-secondary tbody + tbody {
  border-color: #b3b7bb;
}

.table-hover .table-secondary:hover {
  background-color: #c8cbcf;
}

.table-hover .table-secondary:hover > td,
.table-hover .table-secondary:hover > th {
  background-color: #c8cbcf;
}

.table-success,
.table-success > th,
.table-success > td {
  background-color: #c3e6cb;
}

.table-success th,
.table-success td,
.table-success thead th,
.table-success tbody + tbody {
  border-color: #8fd19e;
}

.table-hover .table-success:hover {
  background-color: #b1dfbb;
}

.table-hover .table-success:hover > td,
.table-hover .table-success:hover > th {
  background-color: #b1dfbb;
}

.table-info,
.table-info > th,
.table-info > td {
  background-color: #bee5eb;
}

.table-info th,
.table-info td,
.table-info thead th,
.table-info tbody + tbody {
  border-color: #86cfda;
}

.table-hover .table-info:hover {
  background-color: #abdde5;
}

.table-hover .table-info:hover > td,
.table-hover .table-info:hover > th {
  background-color: #abdde5;
}

.table-warning,
.table-warning > th,
.table-warning > td {
  background-color: #ffeeba;
}

.table-warning th,
.table-warning td,
.table-warning thead th,
.table-warning tbody + tbody {
  border-color: #ffdf7e;
}

.table-hover .table-warning:hover {
  background-color: #ffe8a1;
}

.table-hover .table-warning:hover > td,
.table-hover .table-warning:hover > th {
  background-color: #ffe8a1;
}

.table-danger,
.table-danger > th,
.table-danger > td {
  background-color: #f5c6cb;
}

.table-danger th,
.table-danger td,
.table-danger thead th,
.table-danger tbody + tbody {
  border-color: #ed969e;
}

.table-hover .table-danger:hover {
  background-color: #f1b0b7;
}

.table-hover .table-danger:hover > td,
.table-hover .table-danger:hover > th {
  background-color: #f1b0b7;
}

.table-light,
.table-light > th,
.table-light > td {
  background-color: #fdfdfe;
}

.table-light th,
.table-light td,
.table-light thead th,
.table-light tbody + tbody {
  border-color: #fbfcfc;
}

.table-hover .table-light:hover {
  background-color: #ececf6;
}

.table-hover .table-light:hover > td,
.table-hover .table-light:hover > th {
  background-color: #ececf6;
}

.table-dark,
.table-dark > th,
.table-dark > td {
  background-color: #c6c8ca;
}

.table-dark th,
.table-dark td,
.table-dark thead th,
.table-dark tbody + tbody {
  border-color: #95999c;
}

.table-hover .table-dark:hover {
  background-color: #b9bbbe;
}

.table-hover .table-dark:hover > td,
.table-hover .table-dark:hover > th {
  background-color: #b9bbbe;
}

.table-active,
.table-active > th,
.table-active > td {
  background-color: rgba(0, 0, 0, 0.075);
}

.table-hover .table-active:hover {
  background-color: rgba(0, 0, 0, 0.075);
}

.table-hover .table-active:hover > td,
.table-hover .table-active:hover > th {
  background-color: rgba(0, 0, 0, 0.075);
}

.table .thead-dark th {
  color: #fff;
  background-color: #343a40;
  border-color: #454d55;
}

.table .thead-light th {
  color: #495057;
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.table-dark {
  color: #fff;
  background-color: #343a40;
}

.table-dark th,
.table-dark td,
.table-dark thead th {
  border-color: #454d55;
}

.table-dark.table-bordered {
  border: 0;
}

.table-dark.table-striped tbody tr:nth-of-type(odd) {
  background-color: rgba(255, 255, 255, 0.05);
}

.table-dark.table-hover tbody tr:hover {
  color: #fff;
  background-color: rgba(255, 255, 255, 0.075);
}

@media (max-width: 575.98px) {
  .table-responsive-sm {
    display: block;
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .table-responsive-sm > .table-bordered {
    border: 0;
  }
}

@media (max-width: 767.98px) {
  .table-responsive-md {
    display: block;
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .table-responsive-md > .table-bordered {
    border: 0;
  }
}

@media (max-width: 991.98px) {
  .table-responsive-lg {
    display: block;
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .table-responsive-lg > .table-bordered {
    border: 0;
  }
}

@media (max-width: 1199.98px) {
  .table-responsive-xl {
    display: block;
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .table-responsive-xl > .table-bordered {
    border: 0;
  }
}

.table-responsive {
  display: block;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-responsive > .table-bordered {
  border: 0;
}

.table-responsive {
  display: block;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-responsive > .table-bordered {
  border: 0;
}
.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter, .slide-fade-leave-to
/* .slide-fade-leave-active below version 2.1.8 */ {
  transform: translateX(10px);
  opacity: 0;
}
.scroll-area {
  position: relative;
  margin: auto;
  height: 500px;
}

.ps__scrollbar-x, .ps__scrollbar-y {
  background: #000 !important;
}
</style>